import styled from "@emotion/styled";
import type { HTMLAttributes, ReactNode } from "react";

import {
  Alert,
  Button,
  Icon,
  List,
  ListItem,
  PrimaryButton,
  transientOptions,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

type Props = HTMLAttributes<HTMLDivElement> & {
  onClose: () => void;
  onSubmit: () => void;
  warnings: ReactNode[];
  errors: ReactNode[];
  submitLabel?: string;
};

export const CopilotPopup = ({ errors, warnings, onSubmit, onClose, submitLabel, children, ...htmlAttrs }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  return (
    <ContentContainer {...htmlAttrs}>
      {children}
      <StyledList>
        {errors.map((error, index) => (
          <RowItem key={`error-${index}`} state="error">
            <Message>{error}</Message>
          </RowItem>
        ))}
      </StyledList>

      {!!warnings.length && (
        <StyledAlert color="warning" icon={<Icon icon="exclamation-circle" size={14} />}>
          {formatMessage("copilotPopupWarnings")}
        </StyledAlert>
      )}

      <StyledList>
        {warnings.map((warning, index) => (
          <RowItem key={`warning-${index}`} state="warning">
            <Message>{warning}</Message>
          </RowItem>
        ))}
      </StyledList>

      <ButtonContainer>
        <Button onClick={() => onClose()}>Cancel</Button>
        <PrimaryButton onClick={() => onSubmit()} disabled={!!errors.length}>
          {submitLabel ?? formatMessage("generate")}
        </PrimaryButton>
      </ButtonContainer>
    </ContentContainer>
  );
};

type RowItemProps = {
  children: ReactNode;
  state: "error" | "success" | "warning";
};
const RowItem = ({ children, state }: RowItemProps) => {
  return (
    <Row $state={state}>
      <IconContainer>
        <Icon icon={state === "error" ? "times-circle" : state === "warning" ? "exclamation-circle" : "check-circle"} />
      </IconContainer>
      {children}
    </Row>
  );
};

const StyledList = styled(List)`
  padding: 0;
`;

const StyledAlert = styled(Alert)`
  padding: 0 8px;
  margin: 0 0 5px;
`;

const ContentContainer = styled.div`
  width: 420px;
`;

const IconContainer = styled.span``;

const Message = styled.span`
  flex: 1;
`;

const Row = styled(ListItem, transientOptions)<{ $state: "error" | "success" | "warning" }>`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme, $state }) => ($state === "error" ? theme.error : theme.generalTextIcon)};
  padding: 5px 8px;

  ${IconContainer} {
    color: ${({ theme, $state }) =>
      $state === "error" ? theme.error : $state === "warning" ? theme.warning : theme.tertiary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const formatStrings = {
  copilotPopupWarnings: {
    en: "To improve the quality of results generated, enter more information under Module Settings > AI Assistance.",
  },
  generate: {
    en: "Generate",
  },
};
